import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { formatMoney } from '../../lib/formatMoney';
import { Tooltip } from '../Tooltip';
import { Checkbox } from '../inputs/Checkbox';
import { Select, type SelectOption } from '../inputs/Select';
import type { SelectSort } from '../inputs/selectShared';

/**
 * Seamless in-cell inputs for `<InlineAddTable>` — reference §12.
 *
 * Never a raw `<input>` in a cell. The CELL owns the boundary, not the control: each helper
 * drops its own border and takes the shared focus chrome, so a grid reads as a grid rather
 * than as a stack of boxed fields.
 */
/*
  THE TONE IS SPLIT OUT, and that is not tidying — it is what makes `INVALID_CELL` below able to
  land at all. `bg-transparent` and `bg-red-50/60` are one property at one specificity, so a cell
  carrying both is decided by the order Tailwind happens to emit them in rather than by this
  file: concatenated, the seamless tone won and an invalid cell rendered transparent and slate
  while every class the rule asks for sat in the attribute. MEASURED on the rendered page, which
  is the only thing that could have said so — `tsc`, the lint and the convention scan were all
  clean. So a cell takes ONE tone or the OTHER, and the two never meet in one string.
*/
const SEAMLESS_BASE =
  'block w-full min-h-10 border-0 px-2 text-sm placeholder:text-slate-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-inset ' +
  'disabled:cursor-not-allowed disabled:text-slate-400';

const SEAMLESS_TONE = 'bg-transparent text-slate-600 focus:bg-white focus:ring-blue-500/40';

const SEAMLESS = `${SEAMLESS_BASE} ${SEAMLESS_TONE}`;

/**
 * A CELL WHOSE VALUE IS WRONG, and it is a ring rather than a message.
 *
 * A form field states its rejection in the flow under the control; a grid cell has nowhere to put
 * one without making its row a different height from the rows beside it, which is the thing
 * uniform row height exists to prevent. So the cell carries the colour and the sentence rides in
 * the shared `<Tooltip>`.
 *
 * `ring-inset` for the reason the focus chrome is: a ring drawn outside the box would be clipped
 * by the cell's own border, so the one on the row below would read as the one that is wrong.
 *
 * IT KEEPS ITS RED WHILE FOCUSED, which the seamless tone's `focus:bg-white` would otherwise
 * take away at the one moment the reader is standing in the cell to correct it. The focus ring
 * goes red with it, so the cell still says it has focus without saying it is fine.
 */
const INVALID_CELL =
  'bg-red-50/60 text-red-700 ring-2 ring-inset ring-red-400 focus:ring-red-500/60';

function Cell({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
  return (
    <td className={`border-l border-slate-200 p-0 align-middle first:border-l-0 ${align === 'right' ? 'text-right' : ''}`}>
      {children}
    </td>
  );
}

export function TdInput({
  value,
  onChange,
  placeholder,
  disabled,
  id,
  ariaLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel: string;
}) {
  return (
    <Cell>
      <input
        id={id}
        name={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        className={SEAMLESS}
      />
    </Cell>
  );
}

/**
 * Right-aligned numeric entry. Clamps on commit rather than on keystroke, so typing "1"
 * on the way to "10" is not fought by a min of 5.
 */
export function TdNumber({
  value,
  onChange,
  allowNull = false,
  integerOnly = false,
  min,
  max,
  invalid = null,
  disabled,
  id,
  ariaLabel,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  allowNull?: boolean;
  integerOnly?: boolean;
  min?: number;
  /**
   * A CEILING THE CELL ENFORCES, by clamping on blur.
   *
   * Reach for it only where the bound is a property of the field itself. Where it is one the
   * reader can legitimately type past and needs TELLING about, pass `invalid` instead: silently
   * rewriting a number somebody typed is the failure `draft` above already exists to stop, and a
   * clamp is that failure arriving one keystroke later.
   */
  max?: number;
  /**
   * WHY THIS CELL'S VALUE IS WRONG — non-null paints it and states the reason.
   *
   * It is what lets a grid VALIDATE rather than CLAMP, and the two are not the same promise: a
   * clamp is correct while the ceiling is fixed, and dishonest the moment the ceiling can MOVE
   * underneath a value the reader already typed. Then the number on screen is theirs, the cell
   * says what is wrong with it, and the form's own Save refuses — rather than the figure being
   * quietly rewritten to something they did not choose.
   *
   * The sentence rides in the shared `<Tooltip>` and the box carries `aria-invalid`, because a
   * grid cell has no room for a message under it. The SALN schedule's addressing is the other
   * half: the server names `items.2.orderedQuantity`, `focusValidationError` brings the reader
   * here, and this is what they find when they arrive.
   */
  invalid?: string | null;
  disabled?: boolean;
  id?: string;
  ariaLabel: string;
}) {
  /*
    THE BOX IS ALLOWED TO BE EMPTY WHILE IT IS BEING TYPED INTO — product call, 01 Oct 2026, in
    the reader's own words: *i cant type the "1", it gives me "01"*.

    A non-nullable cell answered an empty box with `onChange(0)`, and that zero rendered
    STRAIGHT BACK into the input. So clearing the cell did not clear it: the reader was left
    with a `0` they could only ever APPEND to, and the next digit read `01`. The cell could not
    be retyped at all, only edited around.

    `draft` is the same answer `<TdMoney>` below already carries — null means "not being typed
    in", so the committed value shows through, and a string means the reader is mid-word and
    the box is theirs. A non-nullable cell left empty still REPORTS zero upward on the
    keystroke, because an empty box is not a value and the form has to know; what changed is
    that the box no longer SHOWS it while the caret is in it.
  */
  const [draft, setDraft] = useState<string | null>(null);

  const box = (
      <input
        id={id}
        name={id}
        type="number"
        aria-invalid={invalid !== null ? true : undefined}
        inputMode={integerOnly ? 'numeric' : 'decimal'}
        step={integerOnly ? 1 : 'any'}
        value={draft ?? (value ?? '')}
        disabled={disabled}
        aria-label={ariaLabel}
        /*
          FOCUS SELECTS WHAT IS THERE, so the first digit REPLACES rather than appends. A grid
          is tabbed across and retyped, which is what every spreadsheet does on focus — and it
          is the other half of the complaint: a cell seeded with the request's whole remainder
          is one the reader almost always wants to overwrite, not extend.
        */
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => {
          const raw = e.target.value;
          setDraft(raw);

          if (raw === '') {
            onChange(allowNull ? null : 0);
            return;
          }

          /*
            A HALF-TYPED VALUE IS LEFT ALONE rather than committed as zero. `-` and `.` parse to
            NaN on the way to a real number, and answering them with 0 is what put a digit in
            front of everything the reader typed next.
          */
          const parsed = integerOnly ? Number.parseInt(raw, 10) : Number.parseFloat(raw);
          if (!Number.isNaN(parsed)) onChange(parsed);
        }}
        onBlur={() => {
          // The draft is the reader's; dropping it hands the cell back to the committed value.
          setDraft(null);

          if (value === null) return;

          let clamped = value;
          if (min !== undefined && clamped < min) clamped = min;
          if (max !== undefined && clamped > max) clamped = max;
          if (clamped !== value) onChange(clamped);
        }}
        className={`${SEAMLESS_BASE} text-right font-mono tabular-nums ${
          invalid !== null ? INVALID_CELL : SEAMLESS_TONE
        }`}
      />
  );

  /*
    THE TOOLTIP WRAPS ONLY A CELL THAT IS WRONG. A grid draws a couple of hundred of these, and a
    portal-rendering component in every one of them is a cost paid by the cells whose values are
    fine — the conditional wrap `<TextCell>` already makes for its own overflow.

    `block`, because the child is `block w-full`: an `inline-flex` anchor would collapse the
    input to its content and the cell would stop filling its column.
  */
  return (
    <Cell align="right">
      {invalid !== null ? (
        <Tooltip label={invalid} block>
          {box}
        </Tooltip>
      ) : (
        box
      )}
    </Cell>
  );
}

/**
 * Groups the INTEGER part of what was typed and leaves the rest exactly as it is.
 *
 * The fraction is deliberately untouched — no rounding, no truncation. Capping it at two
 * places mid-word is the defect this cell used to carry the other way round: a third decimal
 * typed on the way to deleting the second is swallowed, so the reader cannot get there at all.
 * `formatMoney` settles the two places on blur, which is the right moment for it.
 *
 * Grouping is the viewer’s, through `Intl`, so the draft and the committed value use one
 * separator. The DECIMAL point is a literal `.`, which is what the parse below already assumes
 * — say it out loud rather than leave it implicit: a comma-decimal locale would format one way
 * here and parse another, and closing that means changing both ends together.
 */
const INTEGER_FORMAT = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

function groupWhileTyping(typed: string): string {
  const negative = typed.trimStart().startsWith('-');
  const body = typed.replace(/[^0-9.]/g, '');

  const dot = body.indexOf('.');
  const whole = dot === -1 ? body : body.slice(0, dot);

  // Every later dot is dropped: `1.2.3` is not a number on the way to one.
  const fraction = dot === -1 ? null : body.slice(dot + 1).replace(/\./g, '');

  // `BigInt` rather than `Number`, so a long figure is grouped rather than rounded into
  // exponent notation on the way through.
  const grouped = whole === '' ? '' : INTEGER_FORMAT.format(BigInt(whole));

  return `${negative ? '-' : ''}${grouped}${fraction === null ? '' : `.${fraction}`}`;
}

/** The characters that SURVIVE formatting — everything else is a separator we insert. */
const KEPT = /[0-9.-]/;

function keptBefore(text: string, caret: number): number {
  let n = 0;

  for (let i = 0; i < caret && i < text.length; i += 1) {
    if (KEPT.test(text[i])) n += 1;
  }

  return n;
}

/** Where that many surviving characters land in the formatted string. */
function caretAfter(formatted: string, kept: number): number {
  let seen = 0;
  let i = 0;

  while (i < formatted.length && seen < kept) {
    if (KEPT.test(formatted[i])) seen += 1;
    i += 1;
  }

  return i;
}

/**
 * Money, formatted WHILE TYPING — and the caret does not move.
 *
 * <b>Product call, 28 Sep 2026, reversing the format-on-commit decision this cell shipped
 * with.</b> That one is recorded rather than deleted, because its reasoning was correct about
 * the failure and wrong only about the remedy: reformatting a controlled input naively DOES
 * throw the caret to the end of the field, because React writes a new string into the element
 * and the browser has no way to know where the old caret belonged in it. Typing the middle of
 * `2,424,225.00` put one digit in and then jumped.
 *
 * What that bought was a field showing `888888098980` while the reader typed and
 * `888,888,098,980.00` once they left — so the one moment a figure of that size needs its
 * separators, to be READ BACK before it is committed, was the one moment it did not have them.
 *
 * <b>The caret is RESTORED rather than left alone, which is what makes the formatting safe.</b>
 * The separators are the only characters this cell inserts, so the position is expressed in
 * the characters that survive: count them to the left of the caret in what was typed, then
 * walk the formatted string until that many have gone by. Typing, pasting, deleting from the
 * middle and arrowing back all land where the reader expects, whatever the grouping does
 * around them.
 *
 * <b>It is applied in a LAYOUT effect, never in the handler.</b> The element still holds the
 * unformatted text at that point; setting the selection before React has committed the new
 * value puts the caret in a string that is about to be replaced.
 *
 * <b>The DRAFT is still what the field shows while it has focus</b>, and that half is
 * unchanged — it is what lets a value changed from outside (Autogenerate) show through once
 * the field is handed back, and it is why blur clears it rather than writing the formatted
 * string into state.
 *
 * The one thing it does NOT take from `formatMoney` is the em dash: an absent amount reads as
 * `—` on a row being READ and as an empty box on one being FILLED IN, or the reader has to
 * delete a dash before they can type a number.
 */
export function TdMoney({
  value,
  onChange,
  disabled,
  id,
  ariaLabel,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  disabled?: boolean;
  id?: string;
  ariaLabel: string;
}) {
  /* Null means "not being typed in", which is what lets the formatted value show through. */
  const [draft, setDraft] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const caretRef = useRef<number | null>(null);

  /*
    AFTER the commit, never during the handler — the element is still holding the text the
    reader typed while the handler runs, so a selection set there is set in a string React is
    about to replace.
  */
  useLayoutEffect(() => {
    const caret = caretRef.current;
    caretRef.current = null;

    if (caret === null || inputRef.current === null) return;

    inputRef.current.setSelectionRange(caret, caret);
  }, [draft]);

  const display = draft ?? (value === null ? '' : formatMoney(value));

  return (
    <Cell align="right">
      <input
        ref={inputRef}
        id={id}
        name={id}
        type="text"
        inputMode="decimal"
        value={display}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => {
          const typed = e.target.value;
          const caret = e.target.selectionStart ?? typed.length;

          const formatted = groupWhileTyping(typed);

          caretRef.current = caretAfter(formatted, keptBefore(typed, caret));
          setDraft(formatted);

          const parsed = Number.parseFloat(formatted.replace(/[^0-9.-]/g, ''));

          /*
            A half-typed value is null rather than a guess. `-`, `.` and `` all parse to NaN,
            and each is a real moment on the way to a number — reporting 0 for them would put a
            figure on the row that the reader never typed.
          */
          onChange(Number.isNaN(parsed) ? null : parsed);
        }}
        /*
          FOCUS SELECTS WHAT IS THERE, as it does on `<TdNumber>` above and for the same reason:
          a money cell is tabbed into and retyped, so the first keystroke must replace a seeded
          figure rather than land inside it. The caret bookkeeping below is untouched — it runs
          on CHANGE, after the selection has already been replaced.
        */
        onFocus={(e) => e.currentTarget.select()}
        /* Handing the field back is what commits the two decimal places — and dropping the
           draft is what lets a value changed from outside (Autogenerate) show through
           afterwards. */
        onBlur={() => setDraft(null)}
        className={`${SEAMLESS} text-right font-mono tabular-nums`}
      />
    </Cell>
  );
}

/**
 * Wraps the shared `<Select seamless>` so a cell never grows its own dropdown.
 *
 * **`below` puts a second value UNDER the control, in the same cell**, which is what a column
 * that was merged into this one leaves behind — the purchase-order grid's source request number
 * under the request line it belongs to (02 Oct 2026). It is a muted line and never a control:
 * two controls in one cell would be two answers in a slot with one header.
 */
export function TdSelect({
  value,
  onChange,
  options,
  remote = false,
  allowCustom = false,
  onQueryChange,
  onCreateOption,
  sort,
  disabled,
  below,
  id,
  ariaLabel,
}: {
  value: string | null;
  onChange: (next: string) => void;
  options: SelectOption[];
  remote?: boolean;
  allowCustom?: boolean;
  onQueryChange?: (query: string) => void;
  onCreateOption?: (raw: string) => Promise<void> | void;
  /**
   * Forwarded to `<Select>`. Pass `"none"` where the options have a meaningful ORDER — a
   * ladder, a severity, a default-first pair — because the default is alphabetical, which turns
   * Approve/Acknowledge into Acknowledge/Approve and says the wrong one is the ordinary case.
   */
  sort?: SelectSort;
  disabled?: boolean;

  /**
   * A second, read-only value rendered under the control in the same cell.
   *
   * Muted and clipped to the column, like every other seamless value here. Omit it and the
   * cell is byte for byte what it was.
   */
  below?: ReactNode;
  id: string;
  ariaLabel: string;
}) {
  return (
    <Cell>
      <Select
        id={id}
        ariaLabel={ariaLabel}
        seamless
        compact
        value={value}
        onChange={onChange}
        options={options}
        remote={remote}
        allowCustom={allowCustom}
        onQueryChange={onQueryChange}
        onCreateOption={onCreateOption}
        sort={sort}
        disabled={disabled}
      />

      {/*
        THE CONTROL KEEPS ITS OWN HEIGHT and this sits under it, sharing the LEFT EDGE OF THE
        CONTROL'S OWN TEXT — which is `pl-8` on a remote picker and `pl-2` otherwise, because a
        remote one reserves a gutter for the datasource glyph. `px-2` for both was wrong and it
        was visible: on a remote picker the sub-value hung left of the glyph AND of the label,
        so one cell read as two columns. Found by looking at the rendered grid.

        `truncate`, because the column width is authoritative here as it is everywhere else.
      */}
      {below == null ? null : (
        <div
          className={`truncate pr-2 pb-1.5 text-[11px] leading-none text-slate-500 ${
            remote ? 'pl-8' : 'pl-2'
          }`}
        >
          {below}
        </div>
      )}
    </Cell>
  );
}

export function TdDateTime(props: DateLikeProps) {
  return <DateLike {...props} type="datetime-local" />;
}

export function TdDate(props: DateLikeProps) {
  return <DateLike {...props} type="date" />;
}

export function TdTime(props: DateLikeProps) {
  return <DateLike {...props} type="time" />;
}

interface DateLikeProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  id?: string;
  ariaLabel: string;
}

function DateLike({ value, onChange, disabled, id, ariaLabel, type }: DateLikeProps & { type: string }) {
  return (
    <Cell>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={ariaLabel}
        className={`${SEAMLESS} font-mono tabular-nums`}
      />
    </Cell>
  );
}

/** Arbitrary in-cell content at the standard row height. */
export function TdSeamless({
  children,
  align = 'left',
  below,
}: {
  children: ReactNode;
  align?: 'left' | 'right';

  /**
   * A second, read-only value under the first, in the same cell — see `<TdSelect below>`,
   * which this mirrors so a merged column reads the same whether the cell above it is a
   * picker or a stated fact.
   */
  below?: ReactNode;
}) {
  return (
    <Cell align={align}>
      {/*
        THE COLUMN WIDTH IS AUTHORITATIVE, and the VALUE is what yields — the index table's own
        rule, reached the same way. A read-only cell clips rather than spilling past its column,
        and the full value stays reachable through the `title` the caller sets.

        `justify-end` IS WHAT MAKES `align="right"` WORK, and its absence was a real defect:
        `<Cell align>` sets the `<td>`'s text-align, which a FLEX CONTAINER ignores — a flex
        item is positioned by `justify-content`, not by the text alignment of its parent. So
        every right-aligned seamless cell rendered its value on the LEFT while the header above
        it sat on the right, which reads as two columns rather than one.

        It had shipped that way at three call sites — two in the issuance form — because a prop
        that silently does nothing looks decided. Found by looking at the screen.
      */}
      <div
        className={`flex items-center overflow-hidden px-2 ${
          below == null ? 'min-h-10' : 'min-h-0 pt-2'
        } ${align === 'right' ? 'justify-end' : ''}`}
      >
        {children}
      </div>

      {below == null ? null : (
        <div
          className={`truncate px-2 pb-1.5 text-[11px] leading-none text-slate-500 ${
            align === 'right' ? 'text-right' : ''
          }`}
        >
          {below}
        </div>
      )}
    </Cell>
  );
}

/**
 * A YES/NO cell — the shared `<Checkbox>`, centred in its column.
 *
 * It exists because the alternative is a hand-rolled `<td>` at the call site, which is the
 * drift every other cell in this file prevents. Centred rather than left-aligned: a lone
 * checkbox under a centred header reads as belonging to the column, and there is no text
 * beside it to set a left edge against.
 *
 * NO LABEL — the column header is the label, so the box carries `ariaLabel` the way every
 * other cell here does, naming the row it sits on.
 */
export function TdCheckbox({
  checked,
  onChange,
  disabled,
  id,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  id?: string;
  ariaLabel: string;
}) {
  return (
    <Cell>
      <div className="flex items-center justify-center px-3 py-1.5">
        <Checkbox
          id={id}
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          disabled={disabled}
          aria-label={ariaLabel}
        />
      </div>
    </Cell>
  );
}
